import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Shield } from 'lucide-react';
import TexHubLogo from '@/components/brand/TexHubLogo';
import { useLocale } from '@/contexts/LocaleContext';

const PrivacyPage = () => {
  const { isUSA } = useLocale();
  
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <nav className="sticky top-0 z-50 bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <TexHubLogo size="small" />
            </div>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="container mx-auto px-4 max-w-4xl py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">
              {isUSA ? 'Privacy Policy' : 'Aviso de Privacidad'}
            </h1>
            <p className="text-slate-500">{isUSA ? 'Last updated: March 2025' : 'Última actualización: Marzo 2025'}</p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-8 shadow-sm space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">
              {isUSA ? '1. Data Controller' : '1. Responsable del Tratamiento'}
            </h2>
            <p className="text-slate-600 leading-relaxed">
              {isUSA 
                ? 'TexHub ("we", "our" or "the Platform") is responsible for processing your personal data. This Privacy Policy describes how we collect, use, share and protect your personal information.'
                : 'TexHub ("nosotros", "nuestro" o "la Plataforma") es responsable del tratamiento de sus datos personales. Este Aviso de Privacidad describe cómo recopilamos, usamos, compartimos y protegemos su información personal.'}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">
              {isUSA ? '2. Personal Data We Collect' : '2. Datos Personales que Recopilamos'}
            </h2>
            <p className="text-slate-600 leading-relaxed">
              {isUSA ? 'We collect the following types of personal data:' : 'Recopilamos los siguientes tipos de datos personales:'}
            </p>
            <h3 className="font-semibold text-slate-800 mt-4 mb-2">
              {isUSA ? '2.1 Identification Data' : '2.1 Datos de Identificación'}
            </h3>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li>{isUSA ? 'Full name' : 'Nombre completo'}</li>
              <li>{isUSA ? 'Email address' : 'Correo electrónico'}</li>
              <li>{isUSA ? 'Phone number' : 'Número telefónico'}</li>
              <li>{isUSA ? 'Profile picture (optional)' : 'Fotografía de perfil (opcional)'}</li>
            </ul>
            <h3 className="font-semibold text-slate-800 mt-4 mb-2">
              {isUSA ? '2.2 Company Data' : '2.2 Datos de la Empresa'}
            </h3>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li>{isUSA ? 'Company name' : 'Nombre o razón social'}</li>
              <li>{isUSA ? 'Tax ID (optional)' : 'RFC (opcional)'}</li>
              <li>{isUSA ? 'Business address' : 'Dirección comercial'}</li>
              <li>{isUSA ? 'Industry and product/service categories' : 'Giro comercial y categorías de productos/servicios'}</li>
              <li>{isUSA ? 'Company logo' : 'Logo de la empresa'}</li>
            </ul>
            <h3 className="font-semibold text-slate-800 mt-4 mb-2">
              {isUSA ? '2.3 Payment Data' : '2.3 Datos de Pago'}
            </h3>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li>{isUSA ? 'Credit/debit card information (processed by Stripe)' : 'Información de tarjetas de crédito/débito (procesada por Stripe)'}</li>
              <li>{isUSA ? 'Transaction history' : 'Historial de transacciones'}</li>
              <li>{isUSA ? 'Billing data' : 'Datos de facturación'}</li>
            </ul>
            <h3 className="font-semibold text-slate-800 mt-4 mb-2">
              {isUSA ? '2.4 Usage Data' : '2.4 Datos de Uso'}
            </h3>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li>{isUSA ? 'IP address' : 'Dirección IP'}</li>
              <li>{isUSA ? 'Browser and device type' : 'Tipo de navegador y dispositivo'}</li>
              <li>{isUSA ? 'Pages visited and browsing time' : 'Páginas visitadas y tiempo de navegación'}</li>
              <li>{isUSA ? 'Actions taken on the Platform' : 'Acciones realizadas en la Plataforma'}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">
              {isUSA ? '3. Purposes of Processing' : '3. Finalidades del Tratamiento'}
            </h2>
            <p className="text-slate-600 leading-relaxed">
              {isUSA 
                ? 'Your personal data will be used for the following purposes:'
                : 'Sus datos personales serán utilizados para las siguientes finalidades:'}
            </p>
            <h3 className="font-semibold text-slate-800 mt-4 mb-2">
              {isUSA ? 'Primary Purposes (necessary)' : 'Finalidades Primarias (necesarias)'}
            </h3>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li>{isUSA ? 'Create and manage your user account' : 'Crear y administrar su cuenta de usuario'}</li>
              <li>{isUSA ? 'Provide Platform services' : 'Proporcionar los servicios de la Plataforma'}</li>
              <li>{isUSA ? 'Process payments and subscriptions' : 'Procesar pagos y suscripciones'}</li>
              <li>{isUSA ? 'Facilitate communication between users' : 'Facilitar la comunicación entre usuarios'}</li>
              <li>{isUSA ? 'Verify your identity and prevent fraud' : 'Verificar su identidad y prevenir fraudes'}</li>
              <li>{isUSA ? 'Handle support requests' : 'Atender solicitudes de soporte'}</li>
            </ul>
            <h3 className="font-semibold text-slate-800 mt-4 mb-2">
              {isUSA ? 'Secondary Purposes (optional)' : 'Finalidades Secundarias (opcionales)'}
            </h3>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li>{isUSA ? 'Send promotional and marketing communications' : 'Enviar comunicaciones promocionales y de marketing'}</li>
              <li>{isUSA ? 'Perform statistical and market analysis' : 'Realizar análisis estadísticos y de mercado'}</li>
              <li>{isUSA ? 'Improve our products and services' : 'Mejorar nuestros productos y servicios'}</li>
              <li>{isUSA ? 'Personalize your experience on the Platform' : 'Personalizar su experiencia en la Plataforma'}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">
              {isUSA ? '4. Data Transfers' : '4. Transferencias de Datos'}
            </h2>
            <p className="text-slate-600 leading-relaxed">
              {isUSA ? 'Your personal data may be transferred to:' : 'Sus datos personales pueden ser transferidos a:'}
            </p>
            <ul className="list-disc list-inside text-slate-600 mt-3 space-y-2">
              <li><strong>Stripe:</strong> {isUSA ? 'For secure payment processing' : 'Para el procesamiento seguro de pagos'}</li>
              <li><strong>{isUSA ? 'Cloud service providers' : 'Proveedores de servicios en la nube'}:</strong> {isUSA ? 'For storage and processing' : 'Para almacenamiento y procesamiento'}</li>
              <li><strong>{isUSA ? 'Analytics services' : 'Servicios de análisis'}:</strong> {isUSA ? 'To improve the Platform' : 'Para mejorar la Plataforma'}</li>
              <li><strong>{isUSA ? 'Competent authorities' : 'Autoridades competentes'}:</strong> {isUSA ? 'When required by law' : 'Cuando sea requerido por ley'}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">
              {isUSA ? '5. Your Rights' : '5. Derechos ARCO'}
            </h2>
            <p className="text-slate-600 leading-relaxed">
              {isUSA ? 'You have the right to:' : 'Usted tiene derecho a:'}
            </p>
            <ul className="list-disc list-inside text-slate-600 mt-3 space-y-2">
              <li><strong>{isUSA ? 'Access' : 'Acceso'}:</strong> {isUSA ? 'Know what personal data we have about you' : 'Conocer qué datos personales tenemos sobre usted'}</li>
              <li><strong>{isUSA ? 'Rectification' : 'Rectificación'}:</strong> {isUSA ? 'Correct inaccurate or incomplete data' : 'Corregir datos inexactos o incompletos'}</li>
              <li><strong>{isUSA ? 'Deletion' : 'Cancelación'}:</strong> {isUSA ? 'Request the deletion of your data' : 'Solicitar la eliminación de sus datos'}</li>
              <li><strong>{isUSA ? 'Objection' : 'Oposición'}:</strong> {isUSA ? 'Object to the processing of your data for specific purposes' : 'Oponerse al tratamiento de sus datos para fines específicos'}</li>
            </ul>
            <p className="text-slate-600 mt-3">
              {isUSA 
                ? 'To exercise these rights, send a request to privacidad@texhub.com.mx'
                : 'Para ejercer estos derechos, envíe una solicitud a privacidad@texhub.com.mx'}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">
              {isUSA ? '6. Cookies and Tracking Technologies' : '6. Cookies y Tecnologías de Rastreo'}
            </h2>
            <p className="text-slate-600 leading-relaxed">
              {isUSA ? 'We use cookies and similar technologies to:' : 'Utilizamos cookies y tecnologías similares para:'}
            </p>
            <ul className="list-disc list-inside text-slate-600 mt-3 space-y-2">
              <li>{isUSA ? 'Keep your session active' : 'Mantener su sesión activa'}</li>
              <li>{isUSA ? 'Remember your preferences' : 'Recordar sus preferencias'}</li>
              <li>{isUSA ? 'Analyze Platform usage' : 'Analizar el uso de la Plataforma'}</li>
              <li>{isUSA ? 'Improve security' : 'Mejorar la seguridad'}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">
              {isUSA ? '7. Data Security' : '7. Seguridad de los Datos'}
            </h2>
            <p className="text-slate-600 leading-relaxed">
              {isUSA 
                ? 'We implement technical, administrative and physical security measures to protect your personal data, including:'
                : 'Implementamos medidas de seguridad técnicas, administrativas y físicas para proteger sus datos personales, incluyendo:'}
            </p>
            <ul className="list-disc list-inside text-slate-600 mt-3 space-y-2">
              <li>{isUSA ? 'Data encryption in transit and at rest' : 'Encriptación de datos en tránsito y en reposo'}</li>
              <li>{isUSA ? 'Secure authentication' : 'Autenticación segura'}</li>
              <li>{isUSA ? 'Restricted access to personal data' : 'Acceso restringido a datos personales'}</li>
              <li>{isUSA ? 'Security monitoring' : 'Monitoreo de seguridad'}</li>
              <li>{isUSA ? 'PCI-DSS certified payment processing (through Stripe)' : 'Procesamiento de pagos certificado PCI-DSS (a través de Stripe)'}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">
              {isUSA ? '8. Contact' : '8. Contacto'}
            </h2>
            <p className="text-slate-600 leading-relaxed">
              {isUSA 
                ? 'For any questions or comments about this Privacy Policy or the processing of your personal data, you can contact us at:'
                : 'Para cualquier duda o comentario sobre este Aviso de Privacidad o el tratamiento de sus datos personales, puede contactarnos en:'}
            </p>
            <p className="text-slate-600 mt-3">
              <strong>Email:</strong> Info@texhub.com.mx
            </p>
          </section>

          <section className="bg-emerald-50 -mx-8 -mb-8 p-8 rounded-b-xl mt-8">
            <p className="text-sm text-emerald-800">
              {isUSA 
                ? 'By using TexHub, you acknowledge that you have read and understood this Privacy Policy and consent to the processing of your personal data as described herein.'
                : 'Al utilizar TexHub, usted reconoce haber leído y entendido este Aviso de Privacidad y consiente el tratamiento de sus datos personales conforme a lo aquí descrito.'}
            </p>
          </section>
        </div>

        <div className="mt-8 text-center">
          <Link to="/">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              {isUSA ? 'Back to home' : 'Volver al inicio'}
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
